import { useEffect, useRef } from "react";

// App-wide "you have unsaved work" registry (2026-10-01: "add a reminder
// popup for any unsaved edits throughout the app"). Only REAL unsaved work
// registers -- most screens auto-save, so this is about the gaps:
//   - the resume editor's edits before auto-save lands (or when it can't);
//   - a resume save that's still in flight, or that failed (tracked
//     automatically for every page from the save request itself);
//   - text with no auto-save (the cover letter draft).
// UnsavedChangesGuard (App.js) reads this to warn on in-app navigation, the
// Back button, and closing/reloading the tab.
const entries = new Map(); // key -> { message, save }
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn());

export function setUnsaved(key, dirty, { message, save } = {}) {
  if (dirty) entries.set(key, { message, save });
  else entries.delete(key);
  emit();
}
export const hasUnsaved = () => entries.size > 0;
export const unsavedEntries = () => [...entries.entries()].map(([key, v]) => ({ key, ...v }));
export function subscribeUnsaved(fn) { listeners.add(fn); return () => listeners.delete(fn); }

// Register a page's unsaved state. `save` (optional) lets the warning offer
// "Save & leave"; it should resolve truthy when the save succeeded.
export function useUnsavedChanges(key, dirty, { message, save } = {}) {
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    setUnsaved(key, dirty, { message, save: save ? () => saveRef.current?.() : undefined });
    return () => setUnsaved(key, false);
  }, [key, dirty, message, !!save]); // eslint-disable-line react-hooks/exhaustive-deps
}

// --- resume saves, tracked for every page ------------------------------
// POST /api/resumes is the one resume save endpoint (resume-service
// save_resume). While one is in flight, or after one failed (until a later
// one succeeds), the resume isn't safely saved.
const RESUME_SAVE = /\/api\/resumes(\?|$)/;
let inFlight = 0;
let lastFailed = false;
function syncResumeSave() {
  setUnsaved("resume-save", inFlight > 0 || lastFailed, {
    message: inFlight > 0 ? "Your latest resume changes are still saving." : "Your latest resume changes didn't save.",
  });
}
export function trackResumeSaves() {
  if (typeof window === "undefined" || window.__rp360SaveTracker) return;
  window.__rp360SaveTracker = true;
  const orig = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const url = typeof args[0] === "string" ? args[0] : args[0]?.url || "";
    const method = (args[1]?.method || args[0]?.method || "GET").toUpperCase();
    if (method !== "POST" || !RESUME_SAVE.test(url)) return orig(...args);
    inFlight += 1; syncResumeSave();
    try {
      const res = await orig(...args);
      // 4xx like a plan limit isn't "unsaved work" the user can fix by staying.
      lastFailed = res.status >= 500;
      if (res.ok) lastFailed = false;
      return res;
    } catch (e) {
      lastFailed = true;
      throw e;
    } finally {
      inFlight -= 1; syncResumeSave();
    }
  };
}
